import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  TrendingUp, 
  TrendingDown, 
  Wallet,
  AlertCircle
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

interface DashboardStatsProps {
  plan: any;
  isLoading: boolean;
}

export default function DashboardStats({ plan, isLoading }: DashboardStatsProps) {
  const [receivedIncome, setReceivedIncome] = useState<number>(0);
  const [isLoadingIncome, setIsLoadingIncome] = useState<boolean>(false);
  const [actualSpentDaily, setActualSpentDaily] = useState<number>(0);
  const [actualSpentMonthly, setActualSpentMonthly] = useState<number>(0);
  const [plannedDaily, setPlannedDaily] = useState<number>(0);
  const [plannedMonthly, setPlannedMonthly] = useState<number>(0);
  const [loadingExpenses, setLoadingExpenses] = useState<boolean>(false);

  // Função para calcular despesas efetivadas por classificação
  const calculateExpensesByClassification = async () => {
    if (!plan?.id) return;

    setLoadingExpenses(true);
    try {
      // Buscar as categorias vinculadas ao plano mensal
      const { data: plannedCategories, error: plannedError } = await supabase
        .from('planned_categories')
        .select(`
          category_id,
          planned_amount,
          category:financial_categories(id, classification, type)
        `)
        .eq('monthly_plan_id', plan.id);

      if (plannedError) {
        console.error('❌ Erro ao buscar categorias planejadas:', plannedError);
        return;
      }

      const plannedCategoryIds = plannedCategories?.map((pc: any) => pc.category_id) || [];

      if (plannedCategoryIds.length === 0) {
        setActualSpentDaily(0);
        setActualSpentMonthly(0);
        setPlannedDaily(0);
        setPlannedMonthly(0);
        return;
      }

      // Calcular totais planejados por classificação
      let plannedDailyTotal = 0;
      let plannedMonthlyTotal = 0;

      plannedCategories?.forEach((plannedCategory: any) => {
        if (plannedCategory.category?.type === 'expense') {
          const amount = parseFloat(plannedCategory.planned_amount || '0');
          const classification = plannedCategory.category?.classification;
          
          if (classification === 'mensal') {
            plannedMonthlyTotal += amount;
          } else if (classification === 'dia_a_dia') {
            // Considera apenas categorias explicitamente classificadas como 'dia_a_dia'
            plannedDailyTotal += amount;
          }
        }
      });

      setPlannedDaily(plannedDailyTotal);
      setPlannedMonthly(plannedMonthlyTotal);

      // Buscar despesas efetivadas
      const { data: expenses, error } = await supabase
        .from('financial_records')
        .select(`
          amount,
          category_id,
          category:financial_categories(id, classification, type)
        `)
        .eq('monthly_plan_id', plan.id)
        .eq('status', 'affected')
        .in('category_id', plannedCategoryIds);

      if (error) {
        console.error('❌ Erro ao buscar despesas efetivadas:', error);
        return;
      }

      // Filtrar apenas despesas e separar por classificação
      let dailyTotal = 0;
      let monthlyTotal = 0;

      expenses?.forEach((expense: any) => {
        if (expense.category?.type !== 'expense') return;

        const amount = parseFloat(expense.amount || '0');
        const classification = expense.category?.classification;
        
        if (classification === 'mensal') {
          monthlyTotal += amount;
        } else if (classification === 'dia_a_dia') {
          // Considera apenas despesas efetivadas classificadas como 'dia_a_dia'
          dailyTotal += amount;
        }
      });

      setActualSpentDaily(dailyTotal);
      setActualSpentMonthly(monthlyTotal);
    } catch (error) {
      console.error('❌ Erro ao calcular despesas por classificação:', error);
    } finally {
      setLoadingExpenses(false);
    }
  };

  // Buscar receitas com status 'affected' do mês atual
  useEffect(() => {
    const fetchReceivedIncome = async () => {
      if (!plan?.id) return;
      
      setIsLoadingIncome(true);
      try {
        // Buscar todas as receitas do plano mensal com status 'affected'
        const { data: incomes, error } = await supabase
          .from('financial_records')
          .select(`
            amount,
            category:financial_categories(type)
          `)
          .eq('monthly_plan_id', plan.id)
          .eq('status', 'affected');

        if (error) {
          console.error('Erro ao buscar receitas:', error);
          return;
        }

        // Filtrar apenas receitas (tipo 'income') e calcular total
        const totalReceivedIncome = (incomes || [])
          .filter((record: any) => record.category?.type === 'income')
          .reduce((sum, income: any) => {
            return sum + parseFloat(income.amount || '0');
          }, 0);

        setReceivedIncome(totalReceivedIncome);
      } catch (error) {
        console.error('Erro ao buscar receitas recebidas:', error);
      } finally {
        setIsLoadingIncome(false);
      }
    };

    fetchReceivedIncome();
    calculateExpensesByClassification();
  }, [plan?.id]);

  if (isLoading || !plan) {
    return (
      <div className="grid grid-cols-1 gap-3">
        {Array(3).fill(0).map((_, i) => (
          <Card key={i} className="h-12">
            <CardContent className="p-3 h-full flex items-center justify-between">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-6 w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Calcular percentual de receitas recebidas em relação à meta
  const incomeProgress = plan.total_planned_income > 0 
    ? Math.min((receivedIncome / plan.total_planned_income) * 100, 100)
    : 0;

  const expenseProgress = plan.total_planned_expenses > 0 
    ? Math.min((plan.total_spent_expenses / plan.total_planned_expenses) * 100, 100)
    : 0;

  // Calcular progressos por classificação
  const dailyProgress = plannedDaily > 0 ? (actualSpentDaily / plannedDaily) * 100 : 0;
  const monthlyProgress = plannedMonthly > 0 ? (actualSpentMonthly / plannedMonthly) * 100 : 0;

  // Calcular saldo usando receitas efetivadas - despesas mensais efetivadas - despesas diárias efetivadas
  const balance = receivedIncome - actualSpentMonthly - actualSpentDaily;
  const plannedBalance = (plan.total_planned_income || 0) - (plan.total_planned_expenses || 0);

  return (
    <div className="space-y-3">
      {/* Saldo Atual - Compacto */}
      <Card className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-none h-12">
        <CardContent className="p-3 h-full flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet className="w-4 h-4" />
            <span className="text-sm font-medium">Saldo Atual</span>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold">
              {formatCurrency(balance)}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cards de Receitas e Despesas - Layout Responsivo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Receitas */}
        <Card className="h-12">
          <CardContent className="p-3 h-full flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <div>
                <span className="text-sm font-medium text-green-600">Receitas</span>
                <div className="text-xs text-gray-500">
                  {incomeProgress.toFixed(0)}% de {formatCurrency(plan.total_planned_income || 0)}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-green-600">
                {formatCurrency(receivedIncome)}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Despesas Mensais */}
        <Card className="h-12">
          <CardContent className="p-3 h-full flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-red-600" />
              <div>
                <span className="text-sm font-medium text-red-600">Despesas Mensais</span>
                <div className="text-xs text-gray-500 flex items-center gap-1">
                  {monthlyProgress.toFixed(0)}% de {formatCurrency(plannedMonthly)}
                  {loadingExpenses && <div className="w-2 h-2 border border-red-500 border-t-transparent rounded-full animate-spin" />}
                  {monthlyProgress > 100 && <AlertCircle className="w-3 h-3 text-red-600" />}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-red-600">
                {formatCurrency(actualSpentMonthly)}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Despesas Diárias */}
        <Card className="h-12">
          <CardContent className="p-3 h-full flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-orange-600" />
              <div>
                <span className="text-sm font-medium text-orange-600">Despesas Diárias</span>
                <div className="text-xs text-gray-500 flex items-center gap-1">
                  {dailyProgress.toFixed(0)}% de {formatCurrency(plannedDaily)}
                  {loadingExpenses && <div className="w-2 h-2 border border-orange-500 border-t-transparent rounded-full animate-spin" />}
                  {dailyProgress > 100 && <AlertCircle className="w-3 h-3 text-orange-600" />}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-orange-600">
                {formatCurrency(actualSpentDaily)}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
